import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "../../atoms";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import { investorFounderAPI } from "@/services/api/investor.service";

interface InvestorCommentsInboxProps {
  startupId: string;
}

export const InvestorCommentsInbox: React.FC<InvestorCommentsInboxProps> = ({
  startupId,
}) => {
  const queryClient = useQueryClient();
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ["investorComments", startupId],
    queryFn: () => investorFounderAPI.getComments(startupId),
    enabled: Boolean(startupId),
  });

  const replyMutation = useMutation({
    mutationFn: ({ commentId, text }: { commentId: string; text: string }) =>
      investorFounderAPI.replyToComment(startupId, commentId, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investorComments", startupId] });
    },
  });

  const markReadMutation = useMutation({
    mutationFn: () => investorFounderAPI.markCommentsRead(startupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investorComments", startupId] });
    },
  });

  const unreadCount = comments.filter((comment) => !comment.readByFounderAt).length;

  return (
    <SettingsSectionCard
      title="Investor comments"
      description="Comments from investor link viewers, grouped by share link and section."
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-white/55">
          {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
        </div>
        {unreadCount > 0 ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => markReadMutation.mutate()}
          >
            Mark all read
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <div className="text-sm text-white/55">Loading comments...</div>
      ) : comments.length === 0 ? (
        <div className="text-sm text-white/55">No investor comments yet.</div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="rounded-[14px] border border-white/10 bg-surface/40 p-4"
            >
              <div className="flex flex-wrap items-center gap-2 text-xs text-white/50">
                <span className="font-semibold text-white/75">{comment.displayName}</span>
                <span>·</span>
                <span>{comment.linkLabel}</span>
                <span>·</span>
                <span>{comment.section}</span>
                <span>·</span>
                <span>{new Date(comment.createdAt).toLocaleString()}</span>
              </div>
              <p className="mt-2 text-sm text-white/80">{comment.text}</p>
              {comment.replies?.length ? (
                <div className="mt-3 space-y-2 border-l border-white/10 pl-3">
                  {comment.replies.map((reply, index) => (
                    <div key={index} className="text-sm text-white/70">
                      <span className="font-semibold text-white/80">
                        {reply.authorName}:
                      </span>{" "}
                      {reply.text}
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="mt-3 flex gap-2">
                <Input
                  value={replyDrafts[comment.id] ?? ""}
                  onChange={(e) =>
                    setReplyDrafts((prev) => ({
                      ...prev,
                      [comment.id]: e.target.value,
                    }))
                  }
                  placeholder="Write a reply..."
                />
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={
                    replyMutation.isPending || !replyDrafts[comment.id]?.trim()
                  }
                  onClick={() =>
                    replyMutation.mutate({
                      commentId: comment.id,
                      text: replyDrafts[comment.id].trim(),
                    })
                  }
                >
                  Reply
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </SettingsSectionCard>
  );
};
